import { useState, useEffect } from 'react'
import { historyService, type HistoryItem } from '../services/historyService'
import type { MarketingReport } from '../types'

interface HistorySidebarProps {
  isOpen: boolean
  onClose: () => void
  onLoadReport: (report: MarketingReport) => void
}

export function HistorySidebar({ isOpen, onClose, onLoadReport }: HistorySidebarProps) {
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setHistory(historyService.getAllHistory())
    }
  }, [isOpen])

  const handleDelete = (id: string) => {
    if (confirmDelete === id) {
      historyService.deleteReport(id)
      setHistory(historyService.getAllHistory())
      setConfirmDelete(null)
    } else {
      setConfirmDelete(id)
      // Auto-reset confirmation after 3 seconds
      setTimeout(() => setConfirmDelete(null), 3000)
    }
  }

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all history? This cannot be undone.')) {
      historyService.clearHistory()
      setHistory([])
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return 'Today ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-cyber-dark border-l border-cyber-blue/30 z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-cyber-blue/20">
          <h2 className="cyber-heading text-lg">Report History</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-cyber-gray rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* History List */}
        <div className="p-4 h-[calc(100%-140px)] overflow-y-auto">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-400">No reports yet</p>
              <p className="text-sm text-gray-500 mt-1">Generated reports will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="bg-cyber-darker border border-cyber-blue/20 rounded-lg p-3 hover:border-cyber-blue/50 transition-colors group"
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => onLoadReport(item.report)}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          item.mediaType === 'tv' ? 'bg-cyber-pink' : 'bg-cyber-blue'
                        }`} />
                        <h3 className="font-medium text-white truncate">{item.title}</h3>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {item.mediaType === 'tv' ? 'TV Show' : 'Movie'} &bull; {formatDate(item.createdAt)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className={`p-1.5 rounded transition-colors ${
                        confirmDelete === item.id
                          ? 'bg-red-500 text-white'
                          : 'text-gray-500 hover:text-red-400 hover:bg-cyber-gray opacity-0 group-hover:opacity-100'
                      }`}
                      title={confirmDelete === item.id ? 'Click again to confirm' : 'Delete'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {history.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-cyber-blue/20 bg-cyber-dark">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">{history.length} of 20 reports</span>
              <button
                onClick={handleClearAll}
                className="text-sm text-red-400 hover:text-red-300 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
